/**
 * Écarts avec la console Angular comblés le 2026-10-23 (`feat/console-ecarts-legacy`) : réglages et
 * métriques d'un projet, gestes sur les buckets et les entrepôts, préférences. Branchés dans `fr.ts` ;
 * `gestes.en.ts` porte exactement les mêmes clés (typage + `i18n.test.ts`).
 */
export const projectSettingsFr = {
  short: 'Réglages',
  title: 'Réglages du projet',
  subtitle: 'Nom, description, repère et étiquettes du projet {{project}}.',
  labelsHelp: 'Une étiquette par ligne, au format clé=valeur.',
  saved: 'Projet enregistré.',
  errors: {
    labels:
      'Une étiquette par ligne, au format clé=valeur (clé : lettres, chiffres, « . », « _ », « - » ou « / »).',
  },
  danger: {
    title: 'Supprimer le projet',
    description:
      'Ses jobs, ses workflows, leurs runs et les droits portés par le projet sont supprimés avec lui. Les buckets, entrepôts et espaces de travail de l’organisation restent.',
  },
  delete: 'Supprimer le projet',
  confirmDelete:
    'Supprimer le projet « {{name}} » ? Ses jobs, workflows, runs et droits seront supprimés. Cette action est définitive.',
  deleted: 'Projet « {{name}} » supprimé.',
} as const;

export const projectMetricsFr = {
  short: 'Métriques',
  title: 'Métriques du projet',
  subtitle:
    'CPU et mémoire consommés par les pods du projet {{project}} (runs de ses jobs et de ses workflows), relevés toutes les 30 s.',
  window: 'Période',
  windows: { hour: 'Dernière heure', day: '24 heures', week: '7 jours' },
  help: 'Somme des pods du projet à chaque relevé, puis moyenne par pas. Au-delà de 7 jours, un point par heure. Une série vide veut dire qu’aucun pod du projet n’a tourné sur la période.',
} as const;

export const bucketManageFr = {
  new: 'Nouveau bucket',
  newTitle: 'Déclarer un bucket',
  newSubtitle:
    'Un bucket S3 existant (Ceph RGW, MinIO, AWS…) ou un conteneur Azure Blob. Les clés d’accès sont rangées dans le coffre de l’organisation, chiffrées, et ne sont jamais réaffichées.',
  fields: {
    name: 'Nom',
    nameHelp: 'Nom affiché dans la console.',
    description: 'Description',
    type: 'Stockage',
    path: 'Emplacement',
    pathHelp: 's3://nom-du-bucket, éventuellement suivi d’un préfixe.',
    endpoint: 'Point d’accès S3',
    endpointHelp: 'Adresse du service S3, par exemple https://s3.fr-par.example.com.',
    region: 'Région',
    accessKey: 'Clé d’accès',
    secretKey: 'Clé secrète',
    azurePath: 'Conteneur Azure',
    azurePathHelp:
      'https://<compte>.blob.core.windows.net/<conteneur> ou abfss://<conteneur>@<compte>.dfs.core.windows.net, éventuellement suivi d’un préfixe.',
    azureAuth: 'Authentification',
    azureAuthHelp:
      'La clé du compte de stockage, ou un principal de service Entra ID qui a le rôle « Storage Blob Data Contributor » sur le compte ou le conteneur.',
    accountKey: 'Clé du compte de stockage',
    tenantId: 'Tenant Entra ID',
    clientId: 'Identifiant du client (application)',
    clientSecret: 'Secret du client',
    labels: 'Étiquettes',
  },
  typeOptions: {
    s3: 'S3 (Ceph RGW, MinIO, AWS…)',
    azure: 'Azure Blob Storage',
  },
  azureAuthOptions: {
    accountKey: 'Clé du compte de stockage',
    servicePrincipal: 'Principal de service Entra ID',
  },
  errors: {
    path: 'Format attendu : s3://nom-du-bucket, éventuellement suivi d’un préfixe.',
    azurePath:
      'Format attendu : https://<compte>.blob.core.windows.net/<conteneur> ou abfss://<conteneur>@<compte>.dfs.core.windows.net, sans « . » ni « .. ».',
    tenantId: 'Un GUID ou un nom de domaine (contoso.onmicrosoft.com) est attendu.',
    endpoint: 'Adresse http:// ou https:// attendue.',
  },
  created: 'Bucket « {{name}} » déclaré.',
  saved: 'Bucket enregistré.',
  definitionNote:
    'L’emplacement, le type et les clés d’accès ne se modifient pas (le backend les refuse) : déclarez un autre bucket.',
  delete: 'Supprimer le bucket',
  confirmDelete:
    'Supprimer le bucket « {{name}} » ? Sa déclaration et ses clés quittent graal. Les fichiers d’un bucket S3 externe restent chez son hébergeur ; un bucket managé est vidé puis supprimé.',
  deleted: 'Bucket « {{name}} » supprimé.',
  tabs: { definition: 'Définition' },
  folder: {
    new: 'Nouveau dossier',
    title: 'Nouveau dossier',
    description:
      'Un dossier S3 n’existe que par ses objets : graal le crée dans {{path}} en y déposant un fichier vide « keep », comme l’ancienne console.',
    name: 'Nom du dossier',
    nameHelp: 'Un seul niveau, sans « / ».',
    invalid: 'Un nom sans « / », sans espace en tête ni en fin, différent de « . » et « .. ».',
    submit: 'Créer le dossier',
    created: 'Dossier {{path}} créé.',
  },
} as const;

export const warehouseManageFr = {
  new: 'Nouvel entrepôt',
  newTitle: 'Nouvel entrepôt SQL',
  newSubtitle:
    'Un Trino dédié sur une infrastructure de votre organisation, la connexion à une base existante, ou une base PostgreSQL, MySQL ou MariaDB gérée par graal. Un mot de passe est rangé dans le coffre de l’organisation et n’est jamais réaffiché.',
  fields: {
    name: 'Nom',
    description: 'Description',
    engine: 'Moteur',
    engineHelp:
      'Trino : un moteur dédié, déployé par graal sur une infrastructure de votre organisation (il n’y a plus de Trino partagé par la plateforme). Les autres : une base existante, jointe par graal-api. DB2 : le pilote d’IBM n’est pas livré avec graal (licence IBM), l’exploitant de l’installation le fournit — sans lui, « fonction indisponible ».',
    host: 'Hôte',
    port: 'Port',
    portHelp: 'Vide : le port par défaut du moteur (5432, 3306, 1521, 1433 ou 50000 pour DB2).',
    database: 'Base',
    schema: 'Schéma',
    sslMode: 'Chiffrement',
    username: 'Utilisateur',
    password: 'Mot de passe',
    labels: 'Étiquettes',
    account: 'Compte Snowflake',
    accountHelp:
      'Identifiant du compte : orgname-accountname, ou le localisateur avec sa région (xy12345.eu-west-1). graal-api joint <compte>.snowflakecomputing.com, en HTTPS vérifié.',
    computeWarehouse: 'Entrepôt de calcul',
    computeWarehouseHelp:
      'Le virtual warehouse de la session (COMPUTE_WH…). Vide : celui par défaut de l’utilisateur.',
    role: 'Rôle',
    roleHelp:
      'Le rôle de la session ; préférez un rôle en lecture seule. Vide : celui par défaut de l’utilisateur.',
    authentication: 'Authentification',
    privateKey: 'Clé privée',
    privateKeyHelp:
      'Clé PKCS#8 non chiffrée, en PEM (openssl pkcs8 -topk8 -nocrypt). Elle est rangée dans le coffre de l’organisation et n’est jamais réaffichée.',
  },
  engineOptions: {
    trino: 'Trino dédié (sur votre infrastructure)',
    postgresql: 'PostgreSQL',
    mysql: 'MySQL',
    mariadb: 'MariaDB',
    oracle: 'Oracle (base externe)',
    sqlserver: 'SQL Server (base externe)',
    db2: 'IBM DB2 (base externe)',
    snowflake: 'Snowflake (compte externe)',
  },
  sslModes: {
    default: 'Réglage du pilote',
    disable: 'Désactivé',
    require: 'Chiffré, certificat non vérifié',
    verify: 'Chiffré et certificat vérifié',
  },
  authentications: {
    password: 'Mot de passe',
    key_pair: 'Paire de clés',
  },
  errors: {
    host: 'Nom d’hôte ou adresse IP, sans schéma ni port.',
    port: 'Un port entre 1 et 65535.',
    account: 'Identifiant de compte Snowflake : lettres, chiffres, tirets, soulignés et points.',
    identifier: 'Un identifiant Snowflake non quoté : lettres, chiffres, _ et $.',
  },
  created: 'Entrepôt « {{name}} » créé.',
  saved: 'Entrepôt enregistré.',
  definitionNote: 'Le moteur et la connexion ne se modifient pas ici : déclarez un autre entrepôt.',
  delete: 'Supprimer l’entrepôt',
  confirmDelete:
    'Supprimer l’entrepôt « {{name}} » ? Sa déclaration quitte graal. Les tables d’un schéma Trino sont conservées ; une base externe n’est pas touchée.',
  deleted: 'Entrepôt « {{name}} » supprimé.',
  tabs: { definition: 'Définition' },
} as const;

export const preferencesFr = {
  title: 'Préférences',
  subtitle:
    'Réglages de cette console, gardés dans ce navigateur (stockage local) : ils ne suivent pas votre compte d’un poste à l’autre.',
  theme: {
    title: 'Apparence',
    description: 'Sombre par défaut. « Système » suit le réglage de votre poste et change avec lui.',
  },
  language: {
    title: 'Langue',
    description: 'Langue de l’interface. Les noms et descriptions saisis dans graal ne sont pas traduits.',
    names: { fr: 'Français', en: 'English' },
  },
  stored: 'Appliqué et gardé dans ce navigateur.',
} as const;
